import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TabList,
  Tab,
  SelectTabData,
  SelectTabEvent,
  makeStyles,
  shorthands,
  tokens,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
  Spinner,
  Input,
  Textarea,
  Checkbox,
  Label,
  Text,
  Combobox,
} from '@fluentui/react-components';
import {
  PersonAdd20Regular,
  Person24Regular,
  ArrowDownload24Regular,
  ArrowUpload24Regular,
  ArrowSync24Regular,
  Attach24Regular,
  Add20Regular,
  Delete20Regular,
  Document24Regular,
  Image24Regular,
  ClipboardTask24Regular,
  Box24Regular,
} from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { TabbedDialog, TabConfig } from '../components/TabbedDialog';
import CustomerNameWithHoverCard from '../components/CustomerNameWithHoverCard';
import { useSelection } from '../contexts/SelectionContext';
import { getRowMenuForRoute } from '../config/toolbars';
import { apiFetch } from '../lib/apiFetch';
import { formatCalendarDate } from '../lib/businessTime';

const useStyles = makeStyles({
  container: {
    paddingTop: '16px',
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  tabListWrapper: {
    ...shorthands.padding('16px', '16px', '0', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  dataTableWrapper: {
    '& > div': {
      boxShadow: 'none',
      borderRadius: '0',
    },
  },
  taskTypeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  taskTypeIcon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  taskInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  taskInfoTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  taskInfoSubtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
  },
  driverOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  locationDetails: {
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  locationLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  locationValue: {
    fontSize: '14px',
    fontWeight: '500',
  },
  requirementsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  repeatDays: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  dayCheckbox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  itemName: {
    fontSize: '14px',
    fontWeight: '500',
  },
  itemDetails: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  attachmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  attachmentIcon: {
    color: tokens.colorBrandForeground1,
  },
  attachmentInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  attachmentName: {
    fontSize: '14px',
    fontWeight: '500',
  },
  attachmentSize: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('40px'),
    color: tokens.colorNeutralForeground3,
    gap: '8px',
  },
  uploadButton: {
    marginTop: '8px',
  },
});

type TabValue = 'all' | 'unassigned' | 'assigned' | 'loaded' | 'in_transit' | 'arrived' | 'completed' | 'failed';

type TaskType = 'initial_delivery' | 'deliver' | 'exchange' | 'return';

type TaskStatus = 'unassigned' | 'assigned' | 'loaded' | 'in_transit' | 'arrived' | 'completed' | 'failed';

interface Task {
  id: string;
  orderId: string;
  orderNumber: string;
  taskType: TaskType;
  customerId?: string;
  customerName: string;
  customerEmail?: string;
  driver?: string;
  status: TaskStatus;
  scheduledDate: string;
  scheduledTime?: string;
  location: string;
  notes?: string;
}

// Order suggestion for autocomplete
interface OrderSuggestion {
  id: string;
  number: string;
  customerName: string;
  status: string;
  startsAt?: string; // Order delivery/start date
}

// TODO: This should come from an API or config
const DRIVERS = [
  { id: 'carlos', name: 'Carlos Méndez' },
  { id: 'ana', name: 'Ana Torres' },
  { id: 'roberto', name: 'Roberto Flores' },
  { id: 'maria', name: 'Maria Santos' },
];

const getTaskTypeIcon = (type: TaskType) => {
  switch (type) {
    case 'initial_delivery':
      return <ArrowDownload24Regular />;
    case 'deliver':
      return <ArrowDownload24Regular />;
    case 'exchange':
      return <ArrowSync24Regular />;
    case 'return':
      return <ArrowUpload24Regular />;
    default:
      return null;
  }
};

const getTaskTypeLabel = (type: TaskType) => {
  switch (type) {
    case 'initial_delivery':
      return 'Initial Delivery';
    case 'deliver':
      return 'Deliver';
    case 'exchange':
      return 'Exchange';
    case 'return':
      return 'Return';
    default:
      return type;
  }
};

const getStatusBadgeColor = (status: TaskStatus): 'warning' | 'informative' | 'brand' | 'success' | 'danger' | 'important' | 'subtle' => {
  switch (status) {
    case 'unassigned':
      return 'warning';
    case 'assigned':
      return 'informative';
    case 'loaded':
      return 'important';
    case 'in_transit':
      return 'brand';
    case 'arrived':
      return 'success';
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    default:
      return 'warning';
  }
};

const getStatusLabel = (status: TaskStatus) => {
  switch (status) {
    case 'unassigned':
      return 'Unassigned';
    case 'assigned':
      return 'Assigned';
    case 'loaded':
      return 'Loaded';
    case 'in_transit':
      return 'In Transit';
    case 'arrived':
      return 'Arrived';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  // scheduledDate is a calendar day (the time-of-day lives in a separate
  // scheduledTime field), stored at midnight UTC — render in UTC so it never
  // drifts a day for viewers west of UTC.
  return formatCalendarDate(dateString);
};

const Tasks: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const rowMenuItems = getRowMenuForRoute(location.pathname);
  const [selectedTab, setSelectedTab] = useState<TabValue>('all');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const { setSelectedCount, setOnClearSelection } = useSelection();

  // Edit/Delete task state
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

  // Dialog state - unified for both add and edit
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState<Task | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Determine if we're in edit mode
  const isEditMode = taskToEdit !== null;
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [taskToChangeStatus, setTaskToChangeStatus] = useState<Task | null>(null);
  const [newStatus, setNewStatus] = useState<TaskStatus | ''>('');

  // New task form state
  const [newTaskOrder, setNewTaskOrder] = useState<string>(''); // Display value (order #)
  const [newTaskOrderId, setNewTaskOrderId] = useState<string>(''); // UUID of selected order
  const [orderSuggestions, setOrderSuggestions] = useState<OrderSuggestion[]>([]);
  const [searchingOrders, setSearchingOrders] = useState(false);
  const [newTaskType, setNewTaskType] = useState<string>('');
  const [orderDeliveryDate, setOrderDeliveryDate] = useState<string>(''); // Store order's starts_at date
  const [newTaskPriority, setNewTaskPriority] = useState<string>('');
  const [newTaskNotes, setNewTaskNotes] = useState<string>('');
  const [newTaskDate, setNewTaskDate] = useState<string>('');
  const [newTaskDriver, setNewTaskDriver] = useState<string>('');
  const [newTaskRequirements, setNewTaskRequirements] = useState<Record<string, boolean>>({
    signature: false,
    sitePhoto: false,
    landfillReceipt: false,
    customerApproval: false,
    weightTicket: false,
    proofOfDelivery: false,
  });
  const [newTaskRepeatDays, setNewTaskRepeatDays] = useState<Record<string, boolean>>({
    sun: false,
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
  });

  // Tasks data state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update selection context when selection changes
  useEffect(() => {
    setSelectedCount(selectedTaskIds.length);
  }, [selectedTaskIds, setSelectedCount]);

  // Set the clear selection callback
  useEffect(() => {
    setOnClearSelection(() => () => setSelectedTaskIds([]));
  }, [setOnClearSelection]);

  // Clear selection context on unmount
  useEffect(() => {
    return () => {
      setSelectedCount(0);
    };
  }, [setSelectedCount]);

  // Listen for add new task event from toolbar
  useEffect(() => {
    const handleAddNewTask = () => {
      setTaskToEdit(null); // Ensure we're in "add" mode
      resetNewTaskForm();
      setTaskDialogOpen(true);
    };

    window.addEventListener('addNewTask', handleAddNewTask);
    return () => {
      window.removeEventListener('addNewTask', handleAddNewTask);
    };
  }, []);

  // Listen for edit task event from row menu
  useEffect(() => {
    const handleEditTask = (event: CustomEvent<Task>) => {
      const task = event.detail;
      setTaskToEdit(task);
      // Populate form fields with existing task data
      setNewTaskOrder(task.orderNumber || '');
      setNewTaskOrderId(task.orderId || '');
      // Map task type back to display format
      const taskTypeDisplayMap: Record<string, string> = {
        'initial_delivery': 'Initial Delivery',
        'deliver': 'Deliver',
        'exchange': 'Exchange',
        'return': 'Return',
      };
      setNewTaskType(taskTypeDisplayMap[task.taskType] || task.taskType);
      setNewTaskDate(task.scheduledDate || '');
      setNewTaskDriver(task.driver || '');
      setNewTaskNotes(task.notes || '');
      setTaskDialogOpen(true);
    };

    window.addEventListener('editTask', handleEditTask as EventListener);
    return () => {
      window.removeEventListener('editTask', handleEditTask as EventListener);
    };
  }, []);

  // Listen for delete task event from row menu
  useEffect(() => {
    const handleDeleteTask = (event: CustomEvent<Task>) => {
      setTaskToDelete(event.detail);
      setDeleteConfirmOpen(true);
    };

    window.addEventListener('deleteTask', handleDeleteTask as EventListener);
    return () => {
      window.removeEventListener('deleteTask', handleDeleteTask as EventListener);
    };
  }, []);

  // Reset new task form
  const resetNewTaskForm = () => {
    setNewTaskOrder('');
    setNewTaskOrderId('');
    setOrderSuggestions([]);
    setNewTaskType('');
    setOrderDeliveryDate('');
    setNewTaskPriority('');
    setNewTaskNotes('');
    setNewTaskDate('');
    setNewTaskDriver('');
    setNewTaskRequirements({
      signature: false,
      sitePhoto: false,
      landfillReceipt: false,
      customerApproval: false,
      weightTicket: false,
      proofOfDelivery: false,
    });
    setNewTaskRepeatDays({
      sun: false,
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
    });
  };

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setTaskToEdit(null);
    resetNewTaskForm();
  };

  // Search for orders by number (live autocomplete)
  const searchOrders = async (query: string) => {
    if (!query || query.length < 1) {
      setOrderSuggestions([]);
      return;
    }

    setSearchingOrders(true);
    try {
      // Fetch orders - include customer data
      const ordersResponse = await apiFetch(`/booqable/orders?per_page=100`);
      if (!ordersResponse.ok) {
        console.warn('Failed to fetch orders');
        setOrderSuggestions([]);
        return;
      }

      const ordersData = await ordersResponse.json();

      // Build customer lookup
      const customerMap = new Map();
      if (ordersData.included) {
        ordersData.included.forEach((resource: any) => {
          if (resource.type === 'customers') {
            customerMap.set(resource.id, resource.attributes);
          }
        });
      }

      // Normalize query - remove # if present
      const normalizedQuery = query.replace(/^#/, '').toLowerCase();

      // Filter for active orders that match the search query
      const suggestions: OrderSuggestion[] = (ordersData.data || [])
        .filter((order: any) => {
          const orderNumber = (order.attributes?.number || '').toString().toLowerCase();
          const status = order.attributes?.status;
          // Match order number (exact or partial) and only include active orders
          return orderNumber.includes(normalizedQuery) &&
                 (status === 'reserved' || status === 'started');
        })
        .slice(0, 5)
        .map((order: any) => {
          const customerId = order.relationships?.customer?.data?.id;
          const customerData = customerId ? customerMap.get(customerId) : null;
          return {
            id: order.id,
            number: order.attributes?.number?.toString() || 'N/A',
            customerName: customerData?.name || 'Unknown',
            status: order.attributes?.status || 'unknown',
            startsAt: order.attributes?.starts_at || null,
          };
        });

      setOrderSuggestions(suggestions);
    } catch (err) {
      console.error('Error searching orders:', err);
      setOrderSuggestions([]);
    } finally {
      setSearchingOrders(false);
    }
  };

  // Debounced order search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newTaskOrder && !newTaskOrderId) {
        searchOrders(newTaskOrder);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [newTaskOrder, newTaskOrderId]);

  // Handle order selection from suggestions
  const handleOrderSelect = (order: OrderSuggestion) => {
    setNewTaskOrder(order.number);
    setNewTaskOrderId(order.id);
    setOrderSuggestions([]);
    // Store the order's delivery date (convert to YYYY-MM-DD format for input)
    if (order.startsAt) {
      const date = new Date(order.startsAt);
      const formattedDate = date.toISOString().split('T')[0];
      setOrderDeliveryDate(formattedDate);
      // If Initial Delivery is selected, update the scheduled date
      // Otherwise, clear the scheduled date
      if (newTaskType === 'Initial Delivery') {
        setNewTaskDate(formattedDate);
      } else {
        setNewTaskDate('');
      }
    } else {
      setOrderDeliveryDate('');
      // Clear scheduled date when order has no delivery date
      setNewTaskDate('');
    }
  };

  const handleSaveTask = async () => {
    if (!newTaskOrderId || !newTaskType) {
      console.warn('Order and task type are required');
      return;
    }

    setIsSaving(true);
    try {
      // Map task type to API format
      const taskTypeMap: Record<string, string> = {
        'Initial Delivery': 'initial_delivery',
        'Deliver': 'deliver',
        'Exchange': 'exchange',
        'Return': 'return',
      };

      const taskType = taskTypeMap[newTaskType] || newTaskType.toLowerCase();

      if (isEditMode && taskToEdit) {
        // Update existing task via PATCH
        const updateData = {
          orderId: newTaskOrderId,
          orderNumber: newTaskOrder,
          taskType: taskType,
          scheduledDate: newTaskDate || null,
          driver: newTaskDriver || null,
          notes: newTaskNotes || null,
          status: newTaskDriver ? 'assigned' : (taskToEdit.status || 'unassigned'),
        };

        const response = await apiFetch(`/tasks/${taskToEdit.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          throw new Error('Failed to update task');
        }

        // Update local state
        setTasks(tasks.map(task =>
          task.id === taskToEdit.id
            ? { ...task, ...updateData, taskType: taskType as TaskType }
            : task
        ));
      } else {
        // Create new task via POST
        const taskId = crypto.randomUUID();

        const taskData = {
          id: taskId,
          orderId: newTaskOrderId,
          orderNumber: newTaskOrder,
          taskType: taskType,
          scheduledDate: newTaskDate || null,
          driver: newTaskDriver || null,
          notes: newTaskNotes || null,
          status: newTaskDriver ? 'assigned' : 'unassigned',
        };

        const response = await apiFetch(`/tasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(taskData),
        });

        if (!response.ok) {
          throw new Error('Failed to create task');
        }

        // Refresh task list
        await fetchTasks();
      }

      handleCloseTaskDialog();
    } catch (err) {
      console.error('Error saving task:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenStatusDialog = (task: Task) => {
    setTaskToChangeStatus(task);
    setNewStatus(task.status);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setTaskToChangeStatus(null);
    setNewStatus('');
  };

  const handleSaveStatus = async () => {
    if (!taskToChangeStatus || !newStatus) return;

    setIsSaving(true);
    try {
      const response = await apiFetch(`/tasks/${taskToChangeStatus.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update task status');
      }

      // Update local state
      setTasks(tasks.map(task =>
        task.id === taskToChangeStatus.id
          ? { ...task, status: newStatus as TaskStatus }
          : task
      ));

      handleCloseStatusDialog();
    } catch (err) {
      console.error('Error updating task status:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete task handler
  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setIsSaving(true);
    try {
      const response = await apiFetch(`/tasks/${taskToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete task');
      }

      // Remove from local state
      setTasks(tasks.filter(task => task.id !== taskToDelete.id));
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
    } catch (err) {
      console.error('Error deleting task:', err);
    } finally {
      setIsSaving(false);
    }
  };


  // Sync tasks from Booqable orders (creates tasks for orders that don't have them)
  const syncTasksFromOrders = async () => {
    try {
      const response = await apiFetch(`/tasks/sync-from-orders`, {
        method: 'POST',
      });

      if (!response.ok) {
        console.warn('Failed to sync tasks from orders:', response.statusText);
      }
    } catch (err) {
      console.warn('Error syncing tasks from orders:', err);
    }
  };

  // Fetch tasks from the API
  const fetchTasks = async () => {
    try {
      setLoading(true);

      // First sync tasks from Booqable orders
      await syncTasksFromOrders();

      // Then fetch all tasks from our local database
      const response = await apiFetch(`/tasks`);

      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.statusText}`);
      }

      const data = await response.json();
      setTasks(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
    setSelectedTaskIds([]);
  };

  // Calculate counts for each status tab
  const statusCounts = {
    all: tasks.length,
    unassigned: tasks.filter(t => t.status === 'unassigned').length,
    assigned: tasks.filter(t => t.status === 'assigned').length,
    loaded: tasks.filter(t => t.status === 'loaded').length,
    in_transit: tasks.filter(t => t.status === 'in_transit').length,
    arrived: tasks.filter(t => t.status === 'arrived').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    failed: tasks.filter(t => t.status === 'failed').length,
  };

  // Filter tasks based on selected tab
  const filteredTasks = tasks.filter((task) => {
    if (selectedTab === 'all') return true;
    return task.status === selectedTab;
  });

  const handleAssignDriver = (task: Task) => {
    setTaskToAssign(task);
    setSelectedDriver(task.driver || '');
    setAssignDialogOpen(true);
  };

  const handleSaveDriver = async () => {
    if (!taskToAssign || !selectedDriver) return;

    setIsSaving(true);
    try {
      const response = await apiFetch(`/tasks/${taskToAssign.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driver: selectedDriver,
          status: 'assigned',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to assign driver');
      }

      // Update local state - set driver and change status to 'assigned'
      setTasks(tasks.map(task =>
        task.id === taskToAssign.id
          ? { ...task, driver: selectedDriver, status: 'assigned' }
          : task
      ));

      setAssignDialogOpen(false);
      setTaskToAssign(null);
      setSelectedDriver('');
    } catch (err) {
      console.error('Error assigning driver:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Define columns for DataTable
  const taskColumns: ColumnConfig<Task>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      width: '50px',
    },
    {
      columnKey: 'taskType',
      label: 'Task',
      sortable: true,
      render: (task) => (
        <div className={styles.taskTypeCell}>
          <span className={styles.taskTypeIcon}>{getTaskTypeIcon(task.taskType)}</span>
          <span>{getTaskTypeLabel(task.taskType)}</span>
        </div>
      ),
    },
    {
      columnKey: 'orderNumber',
      label: 'Order #',
      sortable: true,
    },
    {
      columnKey: 'customerName',
      label: 'Customer',
      sortable: false,
      render: (task) =>
        task.customerId && task.customerEmail ? (
          <CustomerNameWithHoverCard
            customerId={task.customerId}
            customerName={task.customerName}
            customerEmail={task.customerEmail}
          />
        ) : (
          task.customerName
        ),
    },
    {
      columnKey: 'driver',
      label: 'Driver',
      sortable: true,
      render: (task) => task.driver || (
        <Button
          appearance="subtle"
          size="small"
          icon={<PersonAdd20Regular />}
          title="Assign driver"
          onClick={(e) => {
            e.stopPropagation();
            handleAssignDriver(task);
          }}
        />
      ),
    },
    {
      columnKey: 'status',
      label: 'Status',
      sortable: true,
      render: (task) => (
        <Badge
          appearance="filled"
          color={getStatusBadgeColor(task.status)}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenStatusDialog(task);
          }}
        >
          {getStatusLabel(task.status)}
        </Badge>
      ),
    },
    {
      columnKey: 'scheduledDate',
      label: 'Scheduled',
      sortable: true,
      render: (task) => formatDate(task.scheduledDate),
    },
    {
      columnKey: 'location',
      label: 'Location',
      sortable: false,
    },
  ];

  return (
    <div className={styles.container}>
      {loading && (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          Loading tasks...
        </div>
      )}

      {error && (
        <div style={{ padding: '20px', backgroundColor: '#fef0f0', border: '1px solid #fcd4d4', borderRadius: '4px', color: '#c42b1c', marginTop: '20px' }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && tasks.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#605e5c' }}>
          No tasks found
        </div>
      )}

      {!loading && !error && tasks.length > 0 && (
        <div className={styles.tableContainer}>
          <div className={styles.tabListWrapper}>
            <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
              <Tab value="all">All ({statusCounts.all})</Tab>
              <Tab value="unassigned">Unassigned ({statusCounts.unassigned})</Tab>
              <Tab value="assigned">Assigned ({statusCounts.assigned})</Tab>
              <Tab value="loaded">Loaded ({statusCounts.loaded})</Tab>
              <Tab value="in_transit">In Transit ({statusCounts.in_transit})</Tab>
              <Tab value="arrived">Arrived ({statusCounts.arrived})</Tab>
              <Tab value="completed">Completed ({statusCounts.completed})</Tab>
              <Tab value="failed">Failed ({statusCounts.failed})</Tab>
            </TabList>
          </div>
          <div className={styles.dataTableWrapper}>
            <DataTable
              data={filteredTasks}
              columns={taskColumns}
              keyField="id"
              hideableColumns
              columnVisibilityStorageKey="tasks"
              onSelectionChange={setSelectedTaskIds}
              itemsPerPage={25}
              showCheckboxes={true}
              primaryColumnKey="taskType"
              rowMenuItems={rowMenuItems || undefined}
            />
          </div>
        </div>
      )}

      {/* Change Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={(_, data) => !data.open && handleCloseStatusDialog()}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Change Status</DialogTitle>
            <DialogContent>
              <div className={styles.dialogContent}>
                {taskToChangeStatus && (
                  <div className={styles.taskInfo}>
                    <span className={styles.taskInfoTitle}>
                      {getTaskTypeLabel(taskToChangeStatus.taskType)} - Order #{taskToChangeStatus.orderNumber}
                    </span>
                    <span className={styles.taskInfoSubtitle}>{taskToChangeStatus.customerName}</span>
                  </div>
                )}

                <div className={styles.formField}>
                  <Label className={styles.label}>Status</Label>
                  <Dropdown
                    placeholder="Select status..."
                    value={newStatus ? getStatusLabel(newStatus as TaskStatus) : ''}
                    onOptionSelect={(_, data) => setNewStatus(data.optionValue as TaskStatus)}
                  >
                    <Option value="unassigned" text="Unassigned">Unassigned</Option>
                    <Option value="assigned" text="Assigned">Assigned</Option>
                    <Option value="loaded" text="Loaded">Loaded</Option>
                    <Option value="in_transit" text="In Transit">In Transit</Option>
                    <Option value="arrived" text="Arrived">Arrived</Option>
                    <Option value="completed" text="Completed">Completed</Option>
                    <Option value="failed" text="Failed">Failed</Option>
                  </Dropdown>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCloseStatusDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSaveStatus} disabled={!newStatus || isSaving}>
                {isSaving ? <Spinner size="tiny" /> : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(_, data) => !data.open && setAssignDialogOpen(false)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Assign Driver</DialogTitle>
            <DialogContent>
              <div className={styles.dialogContent}>
                {taskToAssign && (
                  <div className={styles.taskInfo}>
                    <span className={styles.taskInfoTitle}>
                      {getTaskTypeLabel(taskToAssign.taskType)} - Order #{taskToAssign.orderNumber}
                    </span>
                    <span className={styles.taskInfoSubtitle}>{taskToAssign.customerName}</span>
                    <span className={styles.taskInfoSubtitle}>{formatDate(taskToAssign.scheduledDate)}</span>
                  </div>
                )}

                <div className={styles.formField}>
                  <label className={styles.label}>Select Driver</label>
                  <Dropdown
                    placeholder="Choose a driver..."
                    value={selectedDriver}
                    onOptionSelect={(_, data) => setSelectedDriver(data.optionText || '')}
                  >
                    {DRIVERS.map((driver) => (
                      <Option key={driver.id} text={driver.name}>
                        <div className={styles.driverOption}>
                          <Person24Regular />
                          <span>{driver.name}</span>
                        </div>
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAssignDialogOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSaveDriver}
                disabled={isSaving || !selectedDriver}
              >
                {isSaving ? <Spinner size="tiny" /> : 'Assign'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Add/Edit Task Dialog */}
      <TabbedDialog
        isOpen={taskDialogOpen}
        onClose={handleCloseTaskDialog}
        title={isEditMode ? "Edit Task" : "New Task"}
        width="900px"
        contentHeight="50vh"
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: <ClipboardTask24Regular />,
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <Label className={styles.label}>Parent Order</Label>
                    <Combobox
                      placeholder={searchingOrders ? "Searching..." : "Enter order #"}
                      value={newTaskOrder}
                      onInput={(e) => {
                        const value = (e.target as HTMLInputElement).value;
                        setNewTaskOrder(value);
                        setNewTaskOrderId(''); // Clear selected ID when typing
                      }}
                      onOptionSelect={(_, data) => {
                        const selected = orderSuggestions.find(o => o.number === data.optionText);
                        if (selected) {
                          handleOrderSelect(selected);
                        }
                      }}
                      freeform
                    >
                      {orderSuggestions.map((order) => (
                        <Option key={order.id} text={order.number}>
                          #{order.number} - {order.customerName} ({order.status})
                        </Option>
                      ))}
                    </Combobox>
                    {newTaskOrderId && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                        Order validated
                      </Text>
                    )}
                  </div>
                  <div className={styles.formField}>
                    <Label className={styles.label}>Task Type</Label>
                    <Dropdown
                      placeholder="Select type..."
                      value={newTaskType}
                      onOptionSelect={(_, data) => {
                        const selectedType = data.optionText || '';
                        setNewTaskType(selectedType);
                        // Auto-populate scheduled date when Initial Delivery is selected
                        // Clear scheduled date for other task types
                        if (selectedType === 'Initial Delivery' && orderDeliveryDate) {
                          setNewTaskDate(orderDeliveryDate);
                        } else {
                          setNewTaskDate('');
                        }
                      }}
                    >
                      <Option text="Initial Delivery">
                        <div className={styles.driverOption}>
                          <ArrowDownload24Regular />
                          <span>Initial Delivery</span>
                        </div>
                      </Option>
                      <Option text="Deliver">
                        <div className={styles.driverOption}>
                          <ArrowDownload24Regular />
                          <span>Deliver</span>
                        </div>
                      </Option>
                      <Option text="Exchange">
                        <div className={styles.driverOption}>
                          <ArrowSync24Regular />
                          <span>Exchange</span>
                        </div>
                      </Option>
                      <Option text="Return">
                        <div className={styles.driverOption}>
                          <ArrowUpload24Regular />
                          <span>Return</span>
                        </div>
                      </Option>
                    </Dropdown>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <Label className={styles.label}>Priority</Label>
                    <Dropdown
                      placeholder="Select priority..."
                      value={newTaskPriority}
                      onOptionSelect={(_, data) => setNewTaskPriority(data.optionText || '')}
                    >
                      <Option text="Low">Low</Option>
                      <Option text="Normal">Normal</Option>
                      <Option text="High">High</Option>
                      <Option text="Urgent">Urgent</Option>
                    </Dropdown>
                  </div>
                  <div className={styles.formField}>
                    <Label className={styles.label}>Scheduled Date</Label>
                    <Input
                      type="date"
                      value={newTaskDate}
                      onChange={(_, data) => setNewTaskDate(data.value)}
                    />
                  </div>
                </div>

                <div className={styles.formField}>
                  <Label className={styles.label}>Assigned Driver</Label>
                  <Dropdown
                    placeholder="Select driver..."
                    value={newTaskDriver}
                    onOptionSelect={(_, data) => setNewTaskDriver(data.optionText || '')}
                  >
                    {DRIVERS.map((driver) => (
                      <Option key={driver.id} text={driver.name}>
                        <div className={styles.driverOption}>
                          <Person24Regular />
                          <span>{driver.name}</span>
                        </div>
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                <div className={styles.formField}>
                  <Label className={styles.label}>Destination Location</Label>
                  <div className={styles.locationDetails}>
                    {newTaskOrder ? (
                      <>
                        <Text className={styles.locationValue}>123 Main Street</Text>
                        <Text className={styles.locationLabel}>San Juan, PR 00901</Text>
                      </>
                    ) : (
                      <Text className={styles.locationLabel}>Select an order to view location details</Text>
                    )}
                  </div>
                </div>

                <div className={styles.formField}>
                  <Label className={styles.label}>Task Notes</Label>
                  <Textarea
                    placeholder="Add notes for this task..."
                    value={newTaskNotes}
                    onChange={(_, data) => setNewTaskNotes(data.value)}
                    rows={3}
                  />
                </div>

                <div className={styles.formField}>
                  <Label className={styles.label}>Requirements</Label>
                  <div className={styles.requirementsGrid}>
                    <Checkbox
                      checked={newTaskRequirements.signature}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, signature: !!data.checked })}
                      label="Signature"
                    />
                    <Checkbox
                      checked={newTaskRequirements.sitePhoto}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, sitePhoto: !!data.checked })}
                      label="Site Photo"
                    />
                    <Checkbox
                      checked={newTaskRequirements.landfillReceipt}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, landfillReceipt: !!data.checked })}
                      label="Landfill Receipt"
                    />
                    <Checkbox
                      checked={newTaskRequirements.customerApproval}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, customerApproval: !!data.checked })}
                      label="Customer Approval"
                    />
                    <Checkbox
                      checked={newTaskRequirements.weightTicket}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, weightTicket: !!data.checked })}
                      label="Weight Ticket"
                    />
                    <Checkbox
                      checked={newTaskRequirements.proofOfDelivery}
                      onChange={(_, data) => setNewTaskRequirements({ ...newTaskRequirements, proofOfDelivery: !!data.checked })}
                      label="Proof of Delivery"
                    />
                  </div>
                </div>

                <div className={styles.formField}>
                  <Label className={styles.label}>Enable Task Repeat</Label>
                  <div className={styles.repeatDays}>
                    {[
                      { key: 'sun', label: 'Sun' },
                      { key: 'mon', label: 'Mon' },
                      { key: 'tue', label: 'Tue' },
                      { key: 'wed', label: 'Wed' },
                      { key: 'thu', label: 'Thu' },
                      { key: 'fri', label: 'Fri' },
                      { key: 'sat', label: 'Sat' },
                    ].map((day) => (
                      <div key={day.key} className={styles.dayCheckbox}>
                        <Checkbox
                          checked={newTaskRepeatDays[day.key]}
                          onChange={(_, data) => setNewTaskRepeatDays({ ...newTaskRepeatDays, [day.key]: !!data.checked })}
                        />
                        <Text size={200}>{day.label}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: 'items',
            label: 'Items',
            icon: <Box24Regular />,
            content: (
              <>
                {newTaskOrder ? (
                  <div className={styles.itemsList}>
                    <div className={styles.itemRow}>
                      <div className={styles.itemInfo}>
                        <Text className={styles.itemName}>20-Yard Dumpster</Text>
                        <Text className={styles.itemDetails}>SKU: DUMP-20YD • Qty: 1</Text>
                      </div>
                      <Button appearance="subtle" size="small" icon={<Delete20Regular />} />
                    </div>
                    <Button appearance="outline" icon={<Add20Regular />}>
                      Add Item
                    </Button>
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <Text>Select a parent order to view items</Text>
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'attachments',
            label: 'Attachments',
            icon: <Attach24Regular />,
            content: (
              <div className={styles.emptyState}>
                <Attach24Regular />
                <Text>No attachments yet</Text>
                <Button appearance="outline" icon={<Add20Regular />} className={styles.uploadButton}>
                  Upload Attachment
                </Button>
              </div>
            ),
          },
        ]}
        actions={
          <>
            <Button appearance="secondary" onClick={handleCloseTaskDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSaveTask}
              disabled={isSaving || !newTaskOrderId || !newTaskType}
            >
              {isSaving ? <Spinner size="tiny" /> : (isEditMode ? 'Save Changes' : 'Add Task')}
            </Button>
          </>
        }
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(_, data) => !data.open && setDeleteConfirmOpen(false)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete this {taskToDelete?.taskType?.replace('_', ' ')} task
                {taskToDelete?.orderNumber ? ` for order #${taskToDelete.orderNumber}` : ''}?
              </Text>
              <Text style={{ display: 'block', marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
                This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteConfirmOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleConfirmDelete}
                disabled={isSaving}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
              >
                {isSaving ? <Spinner size="tiny" /> : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

    </div>
  );
};

export default Tasks;
